import { useEffect, useMemo, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import {
  ArrowLeft,
  CheckCircle2,
  Download,
  Plus,
  RotateCcw,
  Save,
  Upload,
} from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Alert, AlertDescription } from "~/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { PageHeaderSkeleton, TableSkeleton } from "~/components/ui/skeletons";
import { useAccountsByCompany } from "~/hooks/contabilidad/useAccounts";
import {
  useApproveBudget,
  useBudget,
  useBudgetLines,
  useReopenBudget,
  useReplaceBudgetLines,
  useSaveBudgetLines,
} from "~/hooks/contabilidad/useBudgets";
import { parseGridCellValue } from "~/lib/contabilidad/parseBudgetFile";
import { exportBudgetToExcel } from "~/services/contabilidad/budgetExportService";
import { BUDGET_STATUS_LABELS } from "~/types/contabilidad";

import { AddAccountsDialog } from "./components/AddAccountsDialog";
import { BudgetGrid } from "./components/BudgetGrid";
import { BudgetImportDialog } from "./components/BudgetImportDialog";

import type { Account, BudgetLineInput } from "~/types/contabilidad";

export default function PresupuestoDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: budget, isLoading: isLoadingBudget } = useBudget(id);
  const {
    data: lines = [],
    isLoading: isLoadingLines,
    isFetching: isFetchingLines,
  } = useBudgetLines(id);
  const { data: companyAccounts = [] } = useAccountsByCompany(
    budget?.company_id ?? ""
  );

  const saveMutation = useSaveBudgetLines();
  const replaceMutation = useReplaceBudgetLines();
  const approveMutation = useApproveBudget();
  const reopenMutation = useReopenBudget();

  // Grid state: raw cell strings per account + row account list
  const [rowAccounts, setRowAccounts] = useState<Account[]>([]);
  const [values, setValues] = useState<Record<string, string[]>>({});
  const [initialValues, setInitialValues] = useState<Record<string, number[]>>(
    {}
  );
  const [gridReady, setGridReady] = useState(false);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<
    "approve" | "reopen" | "back" | null
  >(null);

  // Initialize the grid from fetched lines (once, and again after save/import
  // — waiting out in-flight refetches so we never snapshot stale cache)
  useEffect(() => {
    if (gridReady || isLoadingLines || isFetchingLines || !budget) return;

    const accountsById = new Map<string, Account>();
    const nextValues: Record<string, string[]> = {};
    const nextInitial: Record<string, number[]> = {};

    lines.forEach((line) => {
      if (line.account && !accountsById.has(line.account_id)) {
        accountsById.set(line.account_id, line.account);
      }
      if (!nextValues[line.account_id]) {
        nextValues[line.account_id] = Array(12).fill("");
        nextInitial[line.account_id] = Array(12).fill(0);
      }
      nextValues[line.account_id][line.month - 1] =
        line.amount === 0 ? "" : String(line.amount);
      nextInitial[line.account_id][line.month - 1] = line.amount;
    });

    const sorted = Array.from(accountsById.values()).sort((a, b) =>
      a.code.localeCompare(b.code)
    );

    setRowAccounts(sorted);
    setValues(nextValues);
    setInitialValues(nextInitial);
    setGridReady(true);
  }, [gridReady, isLoadingLines, isFetchingLines, lines, budget]);

  const isApproved = budget?.status === "approved";

  // Eligible universe: active ingreso/gasto accounts of the company
  const eligibleAccounts = useMemo(
    () =>
      companyAccounts.filter(
        (account) =>
          account.account_type === "ingreso" ||
          account.account_type === "gasto"
      ),
    [companyAccounts]
  );

  const availableAccounts = useMemo(() => {
    const inGrid = new Set(rowAccounts.map((account) => account.id));
    return eligibleAccounts.filter((account) => !inGrid.has(account.id));
  }, [eligibleAccounts, rowAccounts]);

  // Diff vs the initial snapshot
  const { changedCells, removedAccountIds, isDirty } = useMemo(() => {
    const cells: BudgetLineInput[] = [];
    const inGrid = new Set(rowAccounts.map((account) => account.id));

    rowAccounts.forEach((account) => {
      const rowValues = values[account.id] || Array(12).fill("");
      const rowInitial = initialValues[account.id] || Array(12).fill(0);
      for (let month = 0; month < 12; month++) {
        const parsed = parseGridCellValue(rowValues[month] ?? "");
        if (parsed !== rowInitial[month]) {
          cells.push({ account_id: account.id, month: month + 1, amount: parsed });
        }
      }
    });

    const removed = Object.keys(initialValues).filter(
      (accountId) => !inGrid.has(accountId)
    );

    return {
      changedCells: cells,
      removedAccountIds: removed,
      isDirty: cells.length > 0 || removed.length > 0,
    };
  }, [rowAccounts, values, initialValues]);

  const handleChangeCell = (
    accountId: string,
    monthIndex: number,
    value: string
  ) => {
    setValues((prev) => {
      const row = [...(prev[accountId] || Array(12).fill(""))];
      row[monthIndex] = value;
      return { ...prev, [accountId]: row };
    });
  };

  const handleAddAccounts = (accounts: Account[]) => {
    setRowAccounts((prev) =>
      [...prev, ...accounts].sort((a, b) => a.code.localeCompare(b.code))
    );
    setValues((prev) => {
      const next = { ...prev };
      accounts.forEach((account) => {
        if (!next[account.id]) next[account.id] = Array(12).fill("");
      });
      return next;
    });
  };

  const handleRemoveAccount = (accountId: string) => {
    setRowAccounts((prev) => prev.filter((account) => account.id !== accountId));
  };

  const handleSave = () => {
    if (!budget || !isDirty) return;
    saveMutation.mutate(
      { budgetId: budget.id, changedCells, removedAccountIds },
      { onSuccess: () => setGridReady(false) }
    );
  };

  const handleImport = (importedLines: BudgetLineInput[]) => {
    if (!budget) return;
    replaceMutation.mutate(
      { budgetId: budget.id, lines: importedLines },
      {
        onSuccess: () => {
          setImportDialogOpen(false);
          setGridReady(false);
        },
      }
    );
  };

  const handleExport = () => {
    if (!budget) return;
    exportBudgetToExcel(
      budget,
      budget.company?.name || "",
      rowAccounts.map((account) => ({
        code: account.code,
        name: account.name,
        amounts: (values[account.id] || Array(12).fill("")).map(
          parseGridCellValue
        ),
      }))
    );
  };

  const handleBack = () => {
    if (isDirty) {
      setConfirmAction("back");
    } else {
      navigate("/contabilidad/presupuestos");
    }
  };

  const handleConfirm = () => {
    if (!budget) return;
    if (confirmAction === "approve") {
      approveMutation.mutate(budget.id);
    } else if (confirmAction === "reopen") {
      reopenMutation.mutate(budget.id);
    } else if (confirmAction === "back") {
      navigate("/contabilidad/presupuestos");
    }
    setConfirmAction(null);
  };

  if (isLoadingBudget || (!gridReady && isLoadingLines)) {
    return (
      <PageContainer>
        <PageHeaderSkeleton withAction />
        <TableSkeleton rows={10} columns={8} />
      </PageContainer>
    );
  }

  if (!budget) {
    return (
      <PageContainer>
        <Alert>
          <AlertDescription>Presupuesto no encontrado.</AlertDescription>
        </Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title={`${budget.name} — ${budget.company?.name || ""} ${budget.fiscal_year}`}
        description="Montos mensuales por cuenta, en pesos, tal como se leen en el estado de resultados"
        back={
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 h-9 text-muted-foreground"
            onClick={handleBack}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
        }
        actions={
          <>
            <div className="flex items-center justify-center">
              <Badge
                variant={isApproved ? "default" : "secondary"}
                className={
                  isApproved
                    ? "bg-green-100 text-green-800 hover:bg-green-100"
                    : undefined
                }
              >
                {BUDGET_STATUS_LABELS[budget.status]}
              </Badge>
            </div>
            <Button
              variant="outline"
              className="h-9"
              onClick={() => setImportDialogOpen(true)}
              disabled={isApproved}
            >
              <Upload className="mr-2 h-4 w-4" />
              Importar
            </Button>
            <Button variant="outline" className="h-9" onClick={handleExport}>
              <Download className="mr-2 h-4 w-4" />
              Exportar
            </Button>
            <Button
              variant="outline"
              className="h-9"
              onClick={() => setAddDialogOpen(true)}
              disabled={isApproved}
            >
              <Plus className="mr-2 h-4 w-4" />
              Agregar Cuentas
            </Button>
            {isApproved ? (
              <Button
                variant="outline"
                className="h-9"
                onClick={() => setConfirmAction("reopen")}
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Reabrir
              </Button>
            ) : (
              <Button
                variant="outline"
                className="h-9"
                onClick={() => setConfirmAction("approve")}
                disabled={isDirty}
                title={
                  isDirty ? "Guarda los cambios antes de aprobar" : undefined
                }
              >
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Aprobar
              </Button>
            )}
            <Button
              className="h-9"
              onClick={handleSave}
              disabled={isApproved || !isDirty || saveMutation.isPending}
            >
              <Save className="mr-2 h-4 w-4" />
              {saveMutation.isPending ? "Guardando..." : "Guardar Cambios"}
            </Button>
          </>
        }
      />

      {isApproved && (
        <Alert>
          <AlertDescription>
            Presupuesto aprobado. Reábrelo para editar los montos.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="p-0">
          <BudgetGrid
            accounts={rowAccounts}
            values={values}
            onChangeCell={handleChangeCell}
            onRemoveAccount={handleRemoveAccount}
            disabled={isApproved}
          />
        </CardContent>
      </Card>

      <AddAccountsDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        availableAccounts={availableAccounts}
        onAdd={handleAddAccounts}
      />

      <BudgetImportDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        budget={budget}
        companyName={budget.company?.name || ""}
        accounts={eligibleAccounts}
        onImport={handleImport}
        isImporting={replaceMutation.isPending}
      />

      <AlertDialog
        open={!!confirmAction}
        onOpenChange={(open) => !open && setConfirmAction(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction === "approve" && "Aprobar presupuesto"}
              {confirmAction === "reopen" && "Reabrir presupuesto"}
              {confirmAction === "back" && "Salir sin guardar"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction === "approve" &&
                "Una vez aprobado, las líneas quedan bloqueadas y no se pueden modificar hasta reabrirlo."}
              {confirmAction === "reopen" &&
                "El presupuesto volverá a estado borrador y podrá ser editado."}
              {confirmAction === "back" &&
                "Hay cambios sin guardar que se perderán. ¿Deseas salir?"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm}>
              {confirmAction === "approve" && "Aprobar"}
              {confirmAction === "reopen" && "Reabrir"}
              {confirmAction === "back" && "Salir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
